import { Injectable, inject, signal, computed } from '@angular/core';
import { Observable, of, Subject, BehaviorSubject, from } from 'rxjs';
import { switchMap, catchError, tap, delay, retry } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { IntegrationService } from './integration.service';

export interface QueuedRequest {
  id: string;
  method: string;
  url: string;
  body?: any;
  headers?: Record<string, string>;
  timestamp: number;
  retries: number;
  maxRetries?: number;
}

/**
 * Offline Service
 * Handles offline mode, request queuing, and automatic retry
 */
@Injectable({ providedIn: 'root' })
export class OfflineService {
  private readonly http = inject(HttpClient);
  private readonly integration = inject(IntegrationService);

  private readonly _isOnline = signal<boolean>(navigator.onLine);
  private readonly _queuedRequests = signal<QueuedRequest[]>([]);
  private readonly _processingQueue = signal<boolean>(false);

  private readonly requestQueue: QueuedRequest[] = [];
  private readonly queueSubject = new Subject<QueuedRequest>();
  private readonly onlineSubject = new BehaviorSubject<boolean>(navigator.onLine);

  readonly isOnline = computed(() => this._isOnline());
  readonly queuedRequests = computed(() => this._queuedRequests());
  readonly processingQueue = computed(() => this._processingQueue());

  readonly online$ = this.onlineSubject.asObservable();

  constructor() {
    this.setupOnlineDetection();
    this.setupQueueProcessor();
  }

  /**
   * Setup online/offline detection
   */
  private setupOnlineDetection(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this._isOnline.set(true);
        this.onlineSubject.next(true);
        this.processQueue();
      });

      window.addEventListener('offline', () => {
        this._isOnline.set(false);
        this.onlineSubject.next(false);
      });

      // Initial state
      this._isOnline.set(navigator.onLine);
      this.onlineSubject.next(navigator.onLine);
    }
  }

  /**
   * Queue a request for later processing
   */
  queueRequest(request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retries'>): string {
    const queuedRequest: QueuedRequest = {
      ...request,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      retries: 0,
      maxRetries: request.maxRetries || 3,
    };

    this.requestQueue.push(queuedRequest);
    this._queuedRequests.set([...this.requestQueue]);
    
    // Try to process immediately if online
    if (this._isOnline()) {
      this.processQueue();
    }

    // Persist to localStorage
    this.persistQueue();

    return queuedRequest.id;
  }

  /**
   * Process queued requests
   */
  private processQueue(): void {
    if (this._processingQueue() || !this._isOnline() || this.requestQueue.length === 0) {
      return;
    }

    this._processingQueue.set(true);

    const processNext = () => {
      const request = this.requestQueue.shift();
      if (!request) {
        this._processingQueue.set(false);
        this._queuedRequests.set([...this.requestQueue]);
        this.persistQueue();
        return;
      }

      this.executeRequest(request)
        .pipe(
          catchError((error) => {
            // Retry if retries left
            if (request.retries < (request.maxRetries || 3)) {
              request.retries++;
              this.requestQueue.unshift(request);
              return of(null).pipe(delay(1000 * request.retries)); // Exponential backoff
            } else {
              // Max retries reached, remove from queue
              console.error(`Request ${request.id} failed after ${request.retries} retries:`, error);
              return of(null);
            }
          }),
          tap(() => {
            this._queuedRequests.set([...this.requestQueue]);
            this.persistQueue();
          })
        )
        .subscribe({
          complete: () => {
            processNext();
          },
        });
    };

    processNext();
  }

  /**
   * Execute a queued request
   */
  private executeRequest(request: QueuedRequest): Observable<any> {
    return this.http.request(request.method, request.url, {
      body: request.body,
      headers: request.headers,
    }).pipe(
      retry(1),
      tap(() => {
        // Request successful, remove from queue
        const index = this.requestQueue.findIndex((r) => r.id === request.id);
        if (index !== -1) {
          this.requestQueue.splice(index, 1);
        }
      })
    );
  }

  /**
   * Setup queue processor
   */
  private setupQueueProcessor(): void {
    // Process queue periodically
    setInterval(() => {
      if (this._isOnline() && this.requestQueue.length > 0 && !this._processingQueue()) {
        this.processQueue();
      }
    }, 5000); // Check every 5 seconds

    // Load persisted queue on startup
    this.loadPersistedQueue();
  }

  /**
   * Persist queue to localStorage
   */
  private persistQueue(): void {
    try {
      const queueJson = JSON.stringify(this.requestQueue);
      localStorage.setItem('offline-request-queue', queueJson);
    } catch (error) {
      console.error('Failed to persist request queue:', error);
    }
  }

  /**
   * Load persisted queue from localStorage
   */
  private loadPersistedQueue(): void {
    try {
      const queueJson = localStorage.getItem('offline-request-queue');
      if (queueJson) {
        const queue: QueuedRequest[] = JSON.parse(queueJson);
        // Only keep requests from last 24 hours
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        const validQueue = queue.filter((r) => r.timestamp > oneDayAgo);
        this.requestQueue.push(...validQueue);
        this._queuedRequests.set([...this.requestQueue]);
        
        // Process if online
        if (this._isOnline()) {
          this.processQueue();
        }
      }
    } catch (error) {
      console.error('Failed to load persisted queue:', error);
    }
  }

  /**
   * Clear queue
   */
  clearQueue(): void {
    this.requestQueue.length = 0;
    this._queuedRequests.set([]);
    this.persistQueue();
  }

  /**
   * Remove specific request from queue
   */
  removeRequest(requestId: string): void {
    const index = this.requestQueue.findIndex((r) => r.id === requestId);
    if (index !== -1) {
      this.requestQueue.splice(index, 1);
      this._queuedRequests.set([...this.requestQueue]);
      this.persistQueue();
    }
  }

  /**
   * Get queue stats
   */
  getQueueStats(): {
    total: number;
    oldest: number;
    newest: number;
    pendingRetries: number;
  } {
    if (this.requestQueue.length === 0) {
      return {
        total: 0,
        oldest: 0,
        newest: 0,
        pendingRetries: 0,
      };
    }

    const timestamps = this.requestQueue.map((r) => r.timestamp);
    return {
      total: this.requestQueue.length,
      oldest: Math.min(...timestamps),
      newest: Math.max(...timestamps),
      pendingRetries: this.requestQueue.filter((r) => r.retries > 0).length,
    };
  }
}

